/**
 * useNavigationState — SSOT for the app's tab + sub-tab + chat selection
 * state, plus the localStorage persistence around it.
 *
 * Previously inline in App.tsx (five separate useState hooks + five
 * useEffect persistence hooks + a `handleTabChange` callback). Split
 * out so App.tsx stays thin and any future routing work (URL routing,
 * hash navigation) has a single file to touch.
 *
 * Schema version bump: prefix the storage keys with `v1.` (below). The
 * legacy `nav_*` keys are read-once on first mount, carried forward to
 * the new prefix, and then left alone — users who stop visiting for
 * months can still re-enter and land on their last tab.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { on as onAppEvent } from '../events/bus';
import { trackTabChange } from '../../analytics';

const ROUTE_TO_TAB: Record<string, TabType> = {
    '/':           'home',
    '/assists':    'assistant',
    '/education':  'education',
    '/networking': 'networking',
};

const TAB_TO_ROUTE: Partial<Record<TabType, string>> = {
    home:       '/',
    assistant:  '/assists',
    education:  '/education',
    networking: '/networking',
};

export type TabType =
    | 'home'
    | 'assistant'
    | 'education'
    | 'networking'
    | 'mentorship'
    | 'messages'
    | 'settings'
    | 'profile';
export type EducationSubTab = 'bootcamp' | 'coaching';
export type NetworkingSubTab = 'connections' | 'requests' | 'discover';

/** SSOT for every localStorage key this hook owns. Prefix + version so
 *  a future schema migration is a one-line change here. */
const LS = {
    activeTab:        'v1.nav.activeTab',
    educationSubTab:  'v1.nav.educationSubTab',
    networkingSubTab: 'v1.nav.networkingSubTab',
    selectedChatId:   'v1.nav.selectedChatId',
    selectedProjectId:'v1.nav.selectedProjectId',
} as const;

/** Legacy keys read once on first mount (migrated to the new prefix).
 *  Safe to delete once every active user has logged in at least once. */
const LEGACY_LS = {
    activeTab:        'nav_activeTab',
    educationSubTab:  'nav_educationSubTab',
    networkingSubTab: 'nav_networkingSubTab',
    selectedChatId:   'nav_selectedChatId',
    selectedProjectId:'nav_selectedProjectId',
} as const;

function readLS<T>(key: string, legacyKey: string, fallback: T): T {
    try {
        const raw = localStorage.getItem(key) ?? localStorage.getItem(legacyKey);
        return raw !== null ? (JSON.parse(raw) as T) : fallback;
    } catch { return fallback; }
}
function writeLS(key: string, value: unknown) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* quota */ }
}

export const TAB_TITLES: Record<TabType, string> = {
    home: 'Home',
    assistant: 'Assistant',
    education: 'Education',
    networking: 'Networking',
    mentorship: 'Mentorship',
    messages: 'Messages',
    settings: 'Settings',
    profile: 'Profile',
};

export type NavigationState = {
    activeTab: TabType;
    educationSubTab: EducationSubTab;
    networkingSubTab: NetworkingSubTab;
    selectedChatId: string | null;
    selectedProjectId: string | null;
};

export type NavigationApi = {
    nav: NavigationState;
    setActiveTab: (tab: TabType) => void;
    setEducationSubTab: (sub: EducationSubTab) => void;
    setNetworkingSubTab: (sub: NetworkingSubTab) => void;
    setSelectedChatId: (id: string | null) => void;
    setSelectedProjectId: (id: string | null) => void;
    /** Canonical tab change — tracks analytics + updates the relevant
     *  sub-tab in one call. The only setter App-level callers need. */
    handleTabChange: (tab: TabType, subTab?: string) => void;
};

export function useNavigationState(): NavigationApi {
    const navigate = useNavigate();
    const location = useLocation();

    const [activeTab, setActiveTabState] = useState<TabType>(
        () => ROUTE_TO_TAB[window.location.pathname]
            ?? readLS(LS.activeTab, LEGACY_LS.activeTab, 'home' as TabType),
    );

    // Browser back/forward — sync URL → state
    useEffect(() => {
        const tab = ROUTE_TO_TAB[location.pathname];
        if (tab) setActiveTabState(tab);
    }, [location.pathname]);

    const setActiveTab = useCallback((tab: TabType) => {
        setActiveTabState(tab);
        const route = TAB_TO_ROUTE[tab];
        if (route) navigate(route);
    }, [navigate]);
    const [educationSubTab, setEducationSubTab] = useState<EducationSubTab>(
        () => readLS(LS.educationSubTab, LEGACY_LS.educationSubTab, 'bootcamp' as EducationSubTab),
    );
    const [networkingSubTab, setNetworkingSubTab] = useState<NetworkingSubTab>(() => {
        const raw = readLS<string>(LS.networkingSubTab, LEGACY_LS.networkingSubTab, 'connections');
        return raw === 'connections' || raw === 'requests' || raw === 'discover' ? raw : 'connections';
    });
    const [selectedChatId, setSelectedChatId] = useState<string | null>(
        () => readLS<string | null>(LS.selectedChatId, LEGACY_LS.selectedChatId, null),
    );
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
        () => readLS<string | null>(LS.selectedProjectId, LEGACY_LS.selectedProjectId, null),
    );

    useEffect(() => { writeLS(LS.activeTab, activeTab); }, [activeTab]);
    useEffect(() => { writeLS(LS.educationSubTab, educationSubTab); }, [educationSubTab]);
    useEffect(() => { writeLS(LS.networkingSubTab, networkingSubTab); }, [networkingSubTab]);
    useEffect(() => { writeLS(LS.selectedChatId, selectedChatId); }, [selectedChatId]);
    useEffect(() => { writeLS(LS.selectedProjectId, selectedProjectId); }, [selectedProjectId]);

    const handleTabChange = useCallback((tab: TabType, subTab?: string) => {
        setActiveTab(tab);
        trackTabChange(subTab ? `${tab}:${subTab}` : tab);
        if (tab === 'education' && subTab) {
            setEducationSubTab(subTab as EducationSubTab);
        } else if (tab === 'networking' && subTab) {
            const coerced: NetworkingSubTab =
                subTab === 'connections' || subTab === 'requests' || subTab === 'discover'
                    ? subTab
                    : 'connections';
            setNetworkingSubTab(coerced);
        }
    }, [setActiveTab]);

    // Cross-module: `open-plans` jumps to Settings › Billing. Owned here
    // because it mutates nav state; App.tsx no longer listens.
    useEffect(() => {
        return onAppEvent('open-plans', () => {
            setActiveTab('settings');
            if (typeof window !== 'undefined') {
                window.location.hash = '#billing';
            }
        });
    }, [setActiveTab]);

    // Cross-module: Mentor review list "Review" → Mentorship tab.
    useEffect(() => {
        return onAppEvent('open-submission-review', () => {
            setActiveTab('mentorship');
        });
    }, [setActiveTab]);

    return {
        nav: { activeTab, educationSubTab, networkingSubTab, selectedChatId, selectedProjectId },
        setActiveTab,
        setEducationSubTab,
        setNetworkingSubTab,
        setSelectedChatId,
        setSelectedProjectId,
        handleTabChange,
    };
}
